import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import {
  GridApi,
  IDoesFilterPassParams,
  IFilter,
  IFilterParams,
  IRowNode,
} from 'ag-grid-community'
import { ClientSideSelectFilter } from '../../../../containers/BulkSheetView/components/filter'
import {
  MemberWorkloadStatusType,
  MemberWorkloadStatusRow,
} from '../../MemberWorkloadStatus'
import { getEstimatedActualTypeLabel } from '../../gridOptions/columnDefGroupRowsByMemberCol'
import { allDescendants } from '../../../../containers/BulkSheetView/lib/gridApi'

const isMemberRow = (data?: MemberWorkloadStatusRow) =>
  data?.type === MemberWorkloadStatusType.MEMBER

const isTotalEstimatedActualRow = (data?: MemberWorkloadStatusRow) =>
  data?.type === MemberWorkloadStatusType.TOTAL_ESTIMATED_ACTUAL

const getChildren = (node: IRowNode): IRowNode[] =>
  node.childrenAfterGroup || allDescendants(node)

const passesTypeFilter = (
  api: GridApi,
  childData?: MemberWorkloadStatusRow
): boolean => {
  const typeFilterModel = api.getFilterModel()?.['body.type']

  if (!typeFilterModel || !typeFilterModel.values) {
    return true
  }

  const typeLabel = getEstimatedActualTypeLabel(childData?.estimatedActualType)

  return typeFilterModel.values.includes(typeLabel)
}

export const MemberProjectSelectFilter = forwardRef<IFilter, IFilterParams>(
  (props, ref) => {
    const innerRef = useRef<IFilter | null>(null)

    const passesProjectFilter = (node: IRowNode, data: unknown): boolean => {
      const filterParams = { node, data }
      return innerRef.current?.doesFilterPass?.(filterParams) ?? true
    }

    const hasVisibleChildren = (children: IRowNode[]): boolean => {
      return children.some((child: IRowNode) => {
        const childData = child.data as MemberWorkloadStatusRow | undefined
        return (
          passesProjectFilter(child, childData) &&
          passesTypeFilter(props.api, childData)
        )
      })
    }

    const hasSiblingPassingProjectFilter = (parent: IRowNode): boolean => {
      return getChildren(parent).some((sibling: IRowNode) => {
        const siblingData = sibling.data as MemberWorkloadStatusRow | undefined
        if (
          isTotalEstimatedActualRow(siblingData) ||
          isMemberRow(siblingData)
        ) {
          return false
        }
        return passesProjectFilter(sibling, siblingData)
      })
    }

    useImperativeHandle(ref, () => ({
      doesFilterPass: (params: IDoesFilterPassParams) => {
        const rowData = params.data as MemberWorkloadStatusRow | undefined

        if (isMemberRow(rowData)) {
          return hasVisibleChildren(getChildren(params.node))
        }

        if (isTotalEstimatedActualRow(rowData)) {
          if (!passesTypeFilter(props.api, rowData)) {
            return false
          }

          if (!innerRef.current?.isFilterActive?.()) {
            return true
          }

          const parent = params.node.parent
          return parent ? hasSiblingPassingProjectFilter(parent) : true
        }

        return passesProjectFilter(params.node, rowData)
      },
      isFilterActive: () => innerRef.current?.isFilterActive?.() ?? false,
      getModel: () => innerRef.current?.getModel?.(),
      setModel: (model: unknown) => innerRef.current?.setModel?.(model),
      afterGuiAttached: () => innerRef.current?.afterGuiAttached?.(),
      getModelAsString: (model?: unknown) =>
        innerRef.current?.getModelAsString?.(model) ?? '',
    }))

    return (
      <ClientSideSelectFilter
        ref={innerRef}
        {...(props as unknown as React.ComponentProps<
          typeof ClientSideSelectFilter
        >)}
      />
    )
  }
)

export default MemberProjectSelectFilter
